<template>
  <section class="my-profile position-relative">
    <PageHeader
      showBreadcrumbs
      page-title="My profile"
      wrapperClassName="title-container__my-profile"
      :pageLoading="isLoading"
    >
      <template #buttons>
        <b-button
          class="button title-container__controls-item"
          variant="secondary"
          @click="setVisibleEmailNotificationModal(true)"
        >
          Email notifications
        </b-button>
        <b-button
          class="button title-container__controls-item"
          variant="secondary"
          @click="setVisiblePasswordModal(true)"
        >
          Change password
        </b-button>
        <b-button class="button" variant="primary" @click="onEditProfileClick">
          Edit profile
        </b-button>
      </template>
    </PageHeader>
    <b-modal
      v-model="emailNotifications.showModal"
      size="md"
      :hide-footer="true"
      :style="{ zIndex: 200 }"
      modal-class="email-notification-modal"
      no-close-on-backdrop
      centered
    >
      <template v-slot:modal-header="{ cancel }">
        <h5 class="modal-title">Manage Email notifications</h5>
        <button
          type="button"
          aria-label="Close"
          class="close"
          @click.prevent="cancel"
        >
          <span class="material-icons"> close </span>
        </button>
      </template>
      <FormGenerator
        class="form-email-notifications"
        :data="emailNotifications.form"
        :showFormControls="false"
        @updateItem="updateEmailNotificationsFields"
        ref="form"
      >
      </FormGenerator>
    </b-modal>
    <ChangePasswordModal
      v-model="changePassword.showModal"
      :showCurrentPasswordField="true"
      :onSubmit="handlePasswordSubmit"
      :loading="isLoading"
    />

    <div class="row">
      <div class="col-4">
        <div
          class="content-wrapper h-100 profile-avatar-wrapper d-flex flex-column align-items-center"
        >
          <b-img
            v-if="userAvatar"
            :src="userAvatar"
            rounded="circle"
            alt="Circle image"
            class="user-avatar"
          />
          <b-img
            v-else
            v-bind="mainProps"
            rounded="circle"
            alt="Circle image"
            class="user-avatar"
          />
          <p class="name">{{ userName }}</p>
          <p class="position">{{ userPosition }}</p>
        </div>
      </div>
      <div class="col-4">
        <ProfileInformationTable
          :items="tableData"
          :fields="table.informationTableFields"
        />
      </div>
      <div class="col-4">
        <div class="row" style="margin-bottom: 30px">
          <div class="col-12">
            <ProfileInformationTable
              :items="tableData"
              :fields="table.personalInformationTableFields"
              title="Contact information"
            />
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <UserSkills v-if="$can('read_own-skills')" isMyProfile />
          </div>
        </div>
      </div>
    </div>
    <SlideOutContainerViaPortal :loading="isEditProfileLoading">
      <div v-if="showEdit">
        <EditProfile
          :userId="userId"
          @close="onClose"
          :isUserProfile="false"
          :userData="tableDataEdit"
          @profileUpdate="onProfileUpdate"
          @avatarUpdate="onAvatarUpdate"
          @loadingChange="handleEditProfileLoadingChange"
          :isSliderDataLoading="isEditProfileLoading"
          isShowStickyButtonsAnimation
          :breadcrumbs="[
            {
              text: 'Home',
              to: { name: 'home' },
            },
            {
              text: 'My profile',
              click: () => onClose(),
            }
          ]"
        />
      </div>
    </SlideOutContainerViaPortal>
  </section>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import UserSkills from './UserSkills'
import FormGenerator from '@components/FormGenerator'
import EditProfile from '@components/EditProfile'
import ProfileInformationTable from '@components/tables/ProfileInformationTable'
import UserMixin from '@mixins/UserMixin'
import PageHeader from '@components/PageHeader.vue'
import SlideOutContainerViaPortal from '@components/transition/SlideOutContainerViaPortal.vue'
import ChangePasswordModal from '@components/common/ChangePasswordModal.vue'

export default {
  name: 'MyProfile',
  mixins: [UserMixin],
  components: {
    UserSkills,
    FormGenerator,
    ProfileInformationTable,
    PageHeader,
    SlideOutContainerViaPortal,
    EditProfile,
    ChangePasswordModal
  },
  data () {
    return {
      editProfile: {
        showModal: false,
        form: {
          name: {
            type: 'input',
            name: 'name',
            label: 'Name',
            placeholder: 'Name',
            validation: [{ name: 'required' }],
            value: null,
            class: 'col-12'
          },
          position: {
            type: 'input',
            name: 'position',
            label: 'Position',
            placeholder: 'Position',
            validation: [{ name: 'required' }],
            value: null,
            class: 'col-12'
          }
        }
      },
      emailNotifications: {
        showModal: false,
        form: {
          time_off_requested: {
            type: 'switch',
            name: 'time_off_requested',
            label: 'Time off requested',
            value: true,
            class: 'col-12'
          },
          time_off_approved: {
            type: 'switch',
            name: 'time_off_approved',
            label: 'Time off approved',
            value: true,
            class: 'col-12'
          },
          time_off_deleted: {
            type: 'switch',
            name: 'time_off_deleted',
            label: 'Time off deleted',
            value: true,
            class: 'col-12'
          }
        }
      },
      changePassword: {
        showModal: false
      },
      table: {
        items: {},
        informationTableFields: [
          {
            key: 'payment_type',
            label: 'Payment type'
          },
          {
            key: 'employment_type',
            label: 'Employment type',
            isObject: 'name',
            class: 'text-lowercase'
          },
          {
            key: 'allocations',
            label: 'Allocation',
            isArray: true,
            isObject: 'name'
          },
          {
            key: 'availability',
            label: 'Availability',
            isObject: 'name'
          },
          {
            key: 'years_of_experience',
            label: 'Experience'
          },
          {
            key: 'seniority_lvl',
            label: 'Seniority level',
            isObject: 'name'
          },
          {
            key: 'english_lvl',
            label: 'English level',
            isObject: 'name'
          },
          {
            key: 'roles',
            label: 'Role',
            isArray: true,
            isObject: 'display_name'
          },
          {
            key: 'cv_link',
            label: 'CV',
            isLink: true
          }
        ],
        personalInformationTableFields: [
          {
            key: 'skype',
            label: 'Skype',
            icon: 'skype'
          },
          {
            key: 'telegram',
            label: 'Telegram',
            icon: 'telegram'
          },
          {
            key: 'email',
            label: 'Email',
            icon: 'email'
          },
          {
            key: 'mobile',
            label: 'Phone',
            icon: 'phone'
          },
          {
            key: 'other',
            label: 'Other',
            icon: 'calendar'
          }
        ]
      },

      userId: null,
      userAvatar: null,
      userName: '',
      userPosition: '',
      userOldPassword: '',
      userPassword: '',
      userNewPassword: '',

      mainProps: {
        width: 75,
        height: 75,
        class: 'm1',
        src: '/images/avatar-placeholder.png'
      },

      isLoading: false,
      isEditProfileLoading: false,
      showEdit: false

    }
  },
  computed: {
    ...mapGetters({
      getCurrentUserId: 'getCurrentUserId',
      getLoadedUser: 'users/getLoadedUser'
    }),
    tableData () {
      const data = this.table.items
      const contactData = data.contacts
      return { ...contactData, ...data }
    },
    tableDataEdit () {
      return Object.keys(this.table.items).length ? this.table.items : null
    }

  },
  async mounted () {
    this.isLoading = true

    if (this.getCurrentUserId === this.getLoadedUser?.id) {
      this.userId = this.getLoadedUser.id
      this.userAvatar = this.getLoadedUser.avatar_url
      this.userName = this.getLoadedUser.name
      this.userPosition = this.getLoadedUser.position

      const data = this.getLoadedUser
      this.table.items = { ...data, roles: data.user_roles }
      this.setLoadedUser(this.table.items)

      try {
        await this.getUserSkills()
      } catch (error) {
        this.errorNotifications(error)
      }

      this.isLoading = false
    }

    await this.axios({
      method: 'get',
      url: `/api/authUser`,
      headers: {
        Authorization: 'Bearer' + localStorage.getItem('access_token')
      }
    })
      .then((response) => {
        this.userId = response.data.id
        this.userAvatar = response.data.avatar_url
        this.userName = response.data.name
        this.userPosition = response.data.position

        for (const i in response.data.settings) {
          const settingItem = response.data.settings[i]
          const settingType = settingItem.user_settings
          const settingTypeName = settingType.name
          if (this.emailNotifications.form[settingTypeName]) {
            this.emailNotifications.form[settingTypeName].value =
              settingItem.value
          }
        }
        const data = response.data
        this.table.items = { ...data, roles: data.user_roles }
        this.setLoadedUser(this.table.items)
      })
      .then(async () => {
        await this.getUserSkills()
      })
      .catch(console.error)
      .finally(() => {
        this.isLoading = false
      })
  },
  methods: {
    ...mapActions({
      setLoadedUser: 'users/setLoadedUser'
    }),
    async handlePasswordSubmit (data) {
      const updatedSuccessfully = await this.updatePasswordRequest(data)
      if (updatedSuccessfully) {
        this.setVisiblePasswordModal(false)
      }
    },
    prepareDataForRequest (fields) {
      let data = {}

      for (let key in fields) {
        const field = fields[key]
        const fieldName = field['name']
        const fieldValue = fields[key]['value']

        data[fieldName] = fieldValue
      }

      return data
    },
    submitModal () {
      this.$refs.form.submit()
    },

    setVisibleEditModal (status) {
      this.editProfile.showModal = status
    },
    setVisiblePasswordModal (status) {
      this.changePassword.showModal = status
    },
    setVisibleEmailNotificationModal (status) {
      this.emailNotifications.showModal = status
    },
    async updatePasswordRequest (data) {
      this.isLoading = true
      return this.axios
        .put(`/api/user/update-password/`, data, {
          headers: {
            Authorization: 'Bearer' + localStorage.getItem('access_token')
          }
        })
        .then((response) => {
          if (response.status === 200) {
            this.userOldPassword = ''
            this.userPassword = ''
            this.userNewPassword = ''
            this.$snotify.success(
              'Password succesfully updated',
              'Success',
              this.$store.state.snotify
            )
            this.$store.dispatch('setUser', response.data)
          }
          return true
        })
        .catch((err) => {
          if (err.response.data.errors) {
            this.errorNotifications(err)
            return false
          }
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    updateEmailNotificationsFields (name, field) {
      if (this.emailNotifications.form[name].value !== field.value) {
        this.emailNotifications.form[name].value = field.value
      }
      const data = {}
      data[name] = field.value
      this.axios
        .put(`/api/update/profile/email-notifications`, data, {
          headers: {
            Authorization: 'Bearer' + localStorage.getItem('access_token')
          }
        })
        .then((response) => {
          if (response.status === 200) {
            this.$snotify.success(
              'Email notification updated',
              'Success',
              this.$store.state.snotify
            )
          }
        })
        .catch((err) => {
          if (err.response.data.errors) {
            this.errorNotifications(err)
          }
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    getUserSkills () {
      this.axios({
        method: 'get',
        url: `/api/skills/user/${this.userId}`,
        headers: {
          Authorization: 'Bearer' + localStorage.getItem('access_token')
        }
      })
        .then((response) => {
          this.table.items.skills = response.data
        })
        .catch((err) => {
          this.errorNotifications(err)
        })
    },
    onEditProfileClick (e) {
      e.target.blur()
      const route = this.$router.resolve({
        name: 'my-profile-edit'
      })

      window.history.pushState({}, '', route.href)
      this.showEdit = true
    },
    onClose () {
      const route = this.$router.resolve({ name: 'my-profile' })
      window.history.pushState({}, '', route.href)
      this.showEdit = false
    },
    onProfileUpdate (data) {
      this.userId = data.id
      this.userAvatar = data.avatar_url
      this.userName = data.name
      this.userPosition = data.position

      this.editProfile.form.name.value = data.name
      this.editProfile.form.position.value = data.position

      this.fillEditUserForm(data)
      this.table.items = { ...data, roles: data.roles }
      this.table.items = { ...data, user_roles: data.roles }

      this.$store.dispatch('setUser', {
        ...data,
        user_roles: data.roles,
        roles: data.roles
      })
      this.onClose()
    },
    onAvatarUpdate (avatar) {
      this.userAvatar = avatar
    },
    handleEditProfileLoadingChange (loading) {
      this.isEditProfileLoading = loading
    }

  }

}
</script>

<style scoped lang="scss">
.content-wrapper {
  background-color: #ffffff;
  border-radius: 10px;
}

.profile-avatar-wrapper {
  padding: 30px;
}

.information-wrapper {
  padding: 20px;
}

.user-avatar {
  width: 200px;
  height: 200px;
}

.info-item {
  margin-top: 10px;
}

// Component-specific styles (password form styles moved to _password-forms.scss)
</style>
